import { type FC, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/buttons';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { Skeleton } from '@/components/Skeleton';
import { useToggle } from '@/hooks/common';
import { useNavigation } from '@/hooks/navigation';
import { SPINS_PER_DAY } from '@/modules/promotions/helpers/fortuneWheel';
import { useFortuneWheel } from '@/modules/promotions/hooks/useFortuneWheel';
import { useGetCommonStore } from '@/store';
import { ScFortuneWheelPromoSection } from './styled';

export interface FortuneWheelPromoSectionProps {
  onLinkClick?: () => void;
}

export const FortuneWheelPromoSection: FC<FortuneWheelPromoSectionProps> = ({
  onLinkClick,
}) => {
  const { navigate } = useNavigation();

  const [isFullInfoOpen, toggleFullInfo] = useToggle();

  const { isDailySpinsLimit, spinsLeftAmount } = useFortuneWheel();
  const useCommonStore = useGetCommonStore();

  const { fortuneWheelMenuData, pendingFortuneWheelMenuData } = useCommonStore(
    useShallow((state) => ({
      fortuneWheelMenuData: state.fortuneWheelMenuData,
      pendingFortuneWheelMenuData: state.pendingFortuneWheelMenuData,
    }))
  );

  const handleNavigateToWheel = useCallback(() => {
    navigate('/lucky-wheel');
  }, [navigate]);

  if (pendingFortuneWheelMenuData) return <Skeleton.Rect height={84} />;

  if (!(fortuneWheelMenuData && fortuneWheelMenuData.menu)) return null;

  return (
    <ScFortuneWheelPromoSection
      onClick={toggleFullInfo}
      showFullInfo={isFullInfoOpen}
    >
      <div className="info">
        <div className="level-info">
          <div className="text">
            <span>
              <b className="reward">{fortuneWheelMenuData.menu.reward}</b>
            </span>
            {isDailySpinsLimit ? (
              <span>{fortuneWheelMenuData.menu.tryTomorrow}</span>
            ) : (
              <span>
                <b>{fortuneWheelMenuData.menu.dailySpins}</b>
                {`${spinsLeftAmount}/${SPINS_PER_DAY}`}
              </span>
            )}
          </div>
          <div className="addons">
            <ImageComponent
              alt="character"
              height={44}
              src={fortuneWheelMenuData?.menu?.character?.url || ''}
              width={44}
            />
            <IconComponent name="whiteArrowRight" size="18px" />
          </div>
        </div>
      </div>
      {isFullInfoOpen && (
        <Button
          navigation
          onClick={() => {
            onLinkClick && onLinkClick();
            handleNavigateToWheel();
          }}
        >
          {fortuneWheelMenuData.pageTitle}
        </Button>
      )}
    </ScFortuneWheelPromoSection>
  );
};
